import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Dialog from 'components/Dialog';
import { useForm, useTranslation } from 'hooks';
import Form from './Form';

function CompetenceProfile({
  competenceProfile,
  language = 'de',
  syncErrors,
  syncing,
  onSave
}) {
  const { t, tr } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // Only render if this is a modal route (background state exists)
  const isModal = location.state?.background;
  const isOpen = isModal && location.pathname.includes('/competence-profile');

  const prevSyncingRef = useRef(syncing);

  const { useInput, handleReset, handleSubmit } = useForm({
    initialValues: competenceProfile,
    onSubmit: onSave
  });

  const handleClose = useCallback(() => {
    handleReset?.();
    navigate(`/resume/${id ?? ''}`);
  }, [handleReset, navigate, id]);

  useEffect(() => {
    if (prevSyncingRef.current && !syncing && !syncErrors) {
      handleClose();
    }
    prevSyncingRef.current = syncing;
  }, [syncing, syncErrors, handleClose]);

  // Don't render if not a modal
  if (!isModal) return null;

  return (
    <Dialog
      actionLabel={t('button.save')}
      disabled={syncing}
      open={isOpen}
      fullWidth={true}
      maxWidth="800px"
      syncing={syncing}
      title={tr('title.competenceProfile')}
      subTitle={tr('label.competenceInfoText')}
      onAction={handleSubmit}
      onClose={handleClose}
    >
      <Form data-test-id="competence-profile-form" language={language} useInput={useInput} />
    </Dialog>
  );
}

CompetenceProfile.propTypes = {
  competenceProfile: PropTypes.object,
  language: PropTypes.string,
  syncErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  syncing: PropTypes.bool,
  onSave: PropTypes.func.isRequired
};

export default CompetenceProfile;
